import { Tooltip, TooltipTrigger } from '@vizit/atoms';
import { ComparisonLinkIcon, InfoCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { plural } from '@vizit/utils';
import type * as React from 'react';
import { Button as AriaButton } from 'react-aria-components';

export interface ScoringMeasureGroupProps {
  /**
   * The controls that say what the scores are measured against, e.g. the category picker and the image type chip
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes for the root element
   *
   * @optional
   */
  className?: string;
}

/**
 * Reads "Showing 12 images", the lead-in to a scoring bar's measure group while nothing is scored yet.
 */
export function showingLabel(count: number, noun: 'image' | 'asset') {
  return `Showing ${plural(count, noun)}`;
}

/**
 * Reads "Showing 12 images scored against", the lead-in to a scoring bar's measure group once its scores show.
 */
export function scoredAgainstLabel(count: number, noun: 'image' | 'asset') {
  return `${showingLabel(count, noun)} scored against`;
}

/**
 * The grey band that groups what a grid's scores are measured against (benchmark, product category, image type), so
 * they read as one measure after "Showing N … scored against". The band and the controls in it are square, as in the
 * design, so the band squares off the buttons it holds (the secondary button is otherwise slightly rounded).
 */
export function ScoringMeasureGroup({ children, className }: ScoringMeasureGroupProps) {
  return (
    // `[&_button]:rounded-none` squares every button nested in the band on purpose, so a pill button put here later
    // comes out square too; give it its own wrapper outside the band if it must stay rounded.
    <div className={cx('flex items-center gap-4 bg-gray-utility-100 px-2 py-1 [&_button]:rounded-none', className)}>
      {children}
    </div>
  );
}

/**
 * The link glyph between two controls of a measure group, showing they combine into one measure. Decorative: the
 * controls carry their own names.
 */
export function ScoringMeasureLink() {
  return <ComparisonLinkIcon size={16} className="shrink-0 text-gray-utility-400" aria-hidden />;
}

export interface ScoringMeasureInfoProps {
  /**
   * What the control before the icon means, read in the tooltip
   */
  children: React.ReactNode;
  /**
   * The icon button's accessible name, e.g. "About the benchmark"
   */
  'aria-label': string;
}

/**
 * The info icon after a measure group's control, explaining what it does to the scores. A focusable button, so the
 * tooltip opens on keyboard focus as well as on hover.
 */
export function ScoringMeasureInfo({ children, 'aria-label': ariaLabel }: ScoringMeasureInfoProps) {
  return (
    <TooltipTrigger delay={200}>
      <AriaButton
        aria-label={ariaLabel}
        className="flex shrink-0 cursor-default items-center text-gray-utility-400 outline-none data-[focus-visible]:ring-1 data-[focus-visible]:ring-gray-utility-700"
      >
        <InfoCircleIcon size={16} aria-hidden />
      </AriaButton>
      <Tooltip className="max-w-[240px]">{children}</Tooltip>
    </TooltipTrigger>
  );
}
